/**
 * 探针页：测 bridge.setData / getData 能不能当团队共享模板的存储用。
 *
 * 用法：多维表格 → 插件 → 自定义插件（开发者）→ 填 https://print.xiaomiao.win/?probe=1
 * 三件事要答案：单 key 容量上限、只读协作者能否读、写入是不是按 Base 共享。
 * 结果直接显示在页面上，截图发我即可。
 */
import { useState } from 'react';
import { Button, Typography } from 'antd';
import { bitable } from '@lark-base-open/js-sdk';

const KEY = 'pp_probe_shared';
const SIZES = [1, 10, 50, 100, 200, 500, 1000, 2000]; // KB

export function StorageProbe() {
  const [log, setLog] = useState<string[]>([]);
  const add = (s: string) => setLog((l) => [...l, `${new Date().toLocaleTimeString()}  ${s}`]);

  const identity = async () => {
    try {
      const [uid, tenant, inst] = await Promise.all([
        bitable.bridge.getBaseUserId(),
        bitable.bridge.getTenantKey(),
        bitable.bridge.getInstanceId(),
      ]);
      add(`身份 baseUserId=${uid.slice(0, 10)}… tenant=${tenant.slice(0, 8)}… instance=${inst.slice(0, 10)}…`);
    } catch (e) {
      add(`身份读取失败：${(e as Error).message}`);
    }
  };

  const capacity = async () => {
    for (const kb of SIZES) {
      const payload = 'x'.repeat(kb * 1024);
      const t0 = Date.now();
      try {
        const ok = await bitable.bridge.setData(`pp_probe_${kb}`, payload);
        const back = await bitable.bridge.getData<string>(`pp_probe_${kb}`);
        const same = typeof back === 'string' && back.length === payload.length;
        add(`写 ${kb}KB：setData=${ok} 读回=${same ? '一致' : '不一致(' + String(back).length + ')'} 耗时 ${Date.now() - t0}ms`);
        if (!ok || !same) break;
      } catch (e) {
        add(`写 ${kb}KB 失败：${(e as Error).message}`);
        break;
      }
    }
  };

  const writeShared = async () => {
    try {
      const uid = await bitable.bridge.getBaseUserId();
      const v = { by: uid, at: new Date().toISOString(), note: '如果同事能读到这条，就是按 Base 共享' };
      const ok = await bitable.bridge.setData(KEY, v);
      add(`写共享键 ${KEY}：${ok}（by ${uid.slice(0, 10)}…）`);
    } catch (e) {
      add(`写共享键失败：${(e as Error).message}`);
    }
  };

  const readShared = async () => {
    try {
      const uid = await bitable.bridge.getBaseUserId();
      const v = await bitable.bridge.getData<{ by?: string; at?: string }>(KEY);
      if (!v) add(`读共享键：空（当前账号 ${uid.slice(0, 10)}…）`);
      else add(`读共享键：by=${String(v.by).slice(0, 10)}… at=${v.at}（当前账号 ${uid.slice(0, 10)}…，${v.by === uid ? '就是自己写的' : '是别人写的 → 按 Base 共享成立'}）`);
    } catch (e) {
      add(`读共享键失败：${(e as Error).message}`);
    }
  };

  return (
    <div style={{ padding: 16, fontSize: 13 }}>
      <Typography.Title level={5} style={{ marginTop: 0 }}>存储探针 · bridge.setData / getData</Typography.Title>
      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
        按顺序点。第 3 步在你的账号点，第 4 步换一个<b>只读</b>协作者的账号打开同一个 Base 来点。
      </Typography.Paragraph>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <Button size="small" onClick={identity}>1 · 身份</Button>
        <Button size="small" onClick={capacity}>2 · 容量（1KB → 2MB）</Button>
        <Button size="small" type="primary" onClick={writeShared}>3 · 写共享键</Button>
        <Button size="small" onClick={readShared}>4 · 读共享键（换账号点）</Button>
        <Button size="small" onClick={() => setLog([])}>清空</Button>
      </div>
      <pre style={{ whiteSpace: 'pre-wrap', fontSize: 11.5, lineHeight: 1.7, background: 'var(--surface-2)', padding: 10, borderRadius: 8, minHeight: 120 }}>
        {log.join('\n') || '（还没有结果）'}
      </pre>
    </div>
  );
}
